import type { AgentBlock, AgentEvent } from '@/lib/api/types';

/**
 * The conversation as an ordered block list, plus which message is still open.
 *
 * `openText` is the index of the assistant message currently receiving deltas,
 * or `null`. It exists because a turn is not "text, then extras": a captured
 * turn ran text -> suggestions -> more text, and any event other than a delta
 * closes the open message so the next delta starts a new one. Without it the
 * closing sentence merges into the paragraph that preceded the chips.
 */
export interface TurnState {
  blocks: AgentBlock[];
  openText: number | null;
}

export function emptyTurn(): TurnState {
  return { blocks: [], openText: null };
}

/** Seed from a replayed conversation. Nothing is open: those messages are finished. */
export function fromBlocks(blocks: AgentBlock[]): TurnState {
  return { blocks: [...blocks], openText: null };
}

/** The user's own message. The stream never sends it back, so the composer adds it. */
export function appendUserText(state: TurnState, text: string): TurnState {
  return { blocks: [...state.blocks, { type: 'user_text', text }], openText: null };
}

function append(state: TurnState, block: AgentBlock): TurnState {
  return { blocks: [...state.blocks, block], openText: null };
}

export function reduceAgentEvent(state: TurnState, event: AgentEvent): TurnState {
  switch (event.type) {
    case 'text_delta': {
      if (state.openText !== null) {
        const blocks = [...state.blocks];
        const open = blocks[state.openText];
        if (open?.type === 'assistant_text') {
          blocks[state.openText] = { type: 'assistant_text', text: open.text + event.text };
          return { blocks, openText: state.openText };
        }
      }
      return {
        blocks: [...state.blocks, { type: 'assistant_text', text: event.text }],
        openText: state.blocks.length,
      };
    }

    case 'tool_started':
      return append(state, {
        type: 'tool_activity',
        tool_use_id: event.tool_use_id,
        tool: event.tool,
        ok: true,
        summary: event.title,
      });

    case 'tool_finished': {
      const index = state.blocks.findIndex(
        (block) => block.type === 'tool_activity' && block.tool_use_id === event.tool_use_id,
      );
      // A result for a call we never saw start: drop it rather than invent a
      // line, so a reconnect mid-turn cannot grow phantom activity.
      if (index === -1) return { ...state, openText: null };
      const blocks = [...state.blocks];
      const existing = blocks[index];
      if (existing.type === 'tool_activity') {
        blocks[index] = { ...existing, ok: event.ok, summary: event.summary || existing.summary };
      }
      return { blocks, openText: null };
    }

    case 'chart':
      return append(state, {
        type: 'chart',
        chart_id: event.chart_id,
        kind: event.kind,
        title: event.title,
        x_key: event.x_key,
        y_keys: event.y_keys,
        rows: event.rows,
      });

    case 'suggestions':
      return append(state, { type: 'suggestions', options: event.options });

    case 'confirmation_required':
      return append(state, { type: 'confirmation', actions: event.actions });

    case 'action_resolved': {
      const blocks = state.blocks.map((block) => {
        if (block.type !== 'confirmation') return block;
        if (!block.actions.some((one) => one.id === event.id)) return block;
        return {
          ...block,
          actions: block.actions.map((one) =>
            one.id === event.id ? { ...one, status: event.status } : one,
          ),
        };
      });
      return { blocks, openText: null };
    }

    // `turn_started` only closes whatever was open. `error` and `turn_done` are
    // turn-level state the provider renders outside the transcript: neither is
    // a block, and neither is replayed by GET /agent/conversations/{id}.
    case 'turn_started':
    case 'turn_done':
    case 'error':
      return { ...state, openText: null };

    default:
      return state;
  }
}
